import { Tab, Table, TableBody, TableRow, Tabs } from "@mui/material";
import {
	SaveDraftExteriorTableHeader,
	SaveDraftTableContainer,
	SaveDraftTableDisclaimer,
	SaveDraftTableWrapper
} from "./SaveDraftTable.styles";
import {
	SaveDraftDraftTab,
	SaveDraftModalDetails,
	SaveDraftQuoteTab,
	SaveDraftTableSearchText,
	SaveDraftTemplateTab,
	SaveQuoteModalDetails,
	SaveQuoteTableSearchText,
	SaveTemplateModalDetails,
	SaveTemplateTableSearchText
} from "../../constants";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import SaveDraftNoDrafts from "../SaveDraftNoDrafts";
import SaveDraft400Error from "../SaveDraft400Error";
import TableSkeletonWithTabs from "../../../Common/Table/TableSkeleton/TableSkeletonWithTabs.tsx";
import SortableTableHead from "components/Common/Table/SortableTableHead.tsx";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch";
import SaveDraftTableFilters from "./components/SaveDraftTableFilters.tsx";
import useSession from "hooks/useSession.ts";
import { CacheKeys, InitialDraftFilterOptions } from "pages/OrderPages/OrderListPage/orderListConstants.ts";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import { getFilteredDrafts } from "./saveDraftUtils.ts";
import { models } from "types/api/viewModels.ts";
import SaveDraftNoDraftsFilter from "../SaveDraftNoDraftsFilter.tsx";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { useDrafts } from "hooks/useDrafts.ts";
import SaveDraftTableRow from "components/NewOrders/SaveDraft/SaveDraftTable/components/SaveDraftTableRow/SaveDraftTableRow.tsx";
import {
	draftQuoteTableHeadCells,
	draftTableHeadCells
} from "components/NewOrders/SaveDraft/SaveDraftTable/SaveDraftTable.const.ts";
import { DraftType, DraftTypeType } from "types/declarations/enums.ts";

interface SaveDraftTableProps {
	isLowesUser?: boolean;
	isInternalDesigner?: boolean;
}

const SaveDraftTable = ({ isLowesUser, isInternalDesigner }: SaveDraftTableProps) => {
	const [draftFilterOptions, setDraftFilterOptions] = useSession<FilterOpts>(
		CacheKeys.DraftFilters,
		InitialDraftFilterOptions
	);
	const [templateFilterOptions, setTemplateFilterOptions] = useSession<FilterOpts>(
		CacheKeys.TemplateFilters,
		InitialDraftFilterOptions
	);
	const [newOrderQuoteDraftFilterOptions, setNewOrderQuoteDraftFilterOptions] = useSession<FilterOpts>(
		CacheKeys.NewOrderQuoteDraftFilters,
		InitialDraftFilterOptions
	);
	const [value, setValue] = useState<DraftTypeType>(DraftType.Draft);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const { getDraftsTrigger, allDrafts, getDraftsError, isFetchingDrafts, setDraftViewType, draftViewType } =
		useDrafts();
	const saveDraftDetails = useMemo(() => {
		if (draftViewType === DraftType.Template) {
			return {
				modalDetails: SaveTemplateModalDetails,
				filterConfig: {
					activeView: DraftType.Template,
					filterOptions: templateFilterOptions,
					setFilterOptions: setTemplateFilterOptions
				},
				searchPlaceholder: SaveTemplateTableSearchText,
				subHeader: SaveTemplateModalDetails.SaveTableDisclaimerText
			};
		} else if (draftViewType === DraftType.Quote) {
			return {
				modalDetails: SaveQuoteModalDetails,
				filterConfig: {
					activeView: DraftType.Quote,
					filterOptions: newOrderQuoteDraftFilterOptions,
					setFilterOptions: setNewOrderQuoteDraftFilterOptions
				},
				searchPlaceholder: SaveQuoteTableSearchText,
				subHeader: SaveQuoteModalDetails.SaveTableDisclaimerText
			};
		}
		return {
			modalDetails: SaveDraftModalDetails,
			filterConfig: {
				activeView: DraftType.Draft,
				filterOptions: draftFilterOptions,
				setFilterOptions: setDraftFilterOptions
			},
			searchPlaceholder: SaveDraftTableSearchText,
			subHeader: SaveDraftModalDetails.SaveTableDisclaimerText
		};
	}, [
		draftFilterOptions,
		draftViewType,
		newOrderQuoteDraftFilterOptions,
		setDraftFilterOptions,
		setNewOrderQuoteDraftFilterOptions,
		setTemplateFilterOptions,
		templateFilterOptions
	]);

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("updatedAt", "desc");
	const sortDrafts = useCallback(
		(draftsOrTemplates: models["DraftOrderViewModel"][] | undefined) =>
			draftsOrTemplates?.slice().sort(getComparator(order, orderBy)),
		[order, orderBy, getComparator]
	);
	const [sortedDrafts, setSortedDrafts] = useState(sortDrafts(allDrafts));

	useEffect(() => {
		getDraftsTrigger();
	}, [getDraftsTrigger]);

	useEffect(() => {
		setSortedDrafts(sortDrafts(allDrafts));
	}, [allDrafts, sortDrafts]);

	const handleChange = (_event: SyntheticEvent, newValue: DraftTypeType) => {
		setValue(newValue);
		setDraftViewType(newValue);
	};

	const getDraftNames = (draftsOrTemplates: models["DraftOrderViewModel"][] | undefined): string[] | undefined =>
		draftsOrTemplates?.filter(Boolean).map((draft: models["DraftOrderViewModel"]) => draft?.draftName ?? "");

	const searchOptions = useMemo(() => getDraftNames(allDrafts), [allDrafts]);

	const handleDraftSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? null);
	};

	const filteredDrafts = useMemo(() => {
		if (draftViewType === DraftType.Draft) {
			return getFilteredDrafts(draftFilterOptions, sortedDrafts ?? []);
		} else if (draftViewType === DraftType.Template) {
			return getFilteredDrafts(templateFilterOptions, sortedDrafts ?? []);
		} else {
			return getFilteredDrafts(newOrderQuoteDraftFilterOptions, sortedDrafts ?? []);
		}
	}, [draftFilterOptions, draftViewType, sortedDrafts, templateFilterOptions, newOrderQuoteDraftFilterOptions]);

	const filteredDraftRows = filteredDrafts?.filter((draft) =>
		draft.draftName?.toUpperCase()?.includes(searchValue?.toUpperCase() ?? "")
	);

	const filteredHeadCells = useMemo(
		() =>
			draftViewType === DraftType.Quote
				? draftQuoteTableHeadCells
				: draftTableHeadCells.filter(
						(cell) => !(draftViewType === DraftType.Template && cell.id === "orderType")
					),
		[draftViewType]
	);

	const isAvailable = !isFetchingDrafts;
	const filteringDrafts = filteredDraftRows?.length > 0;

	const draftRows = filteringDrafts ? (
		<SaveDraftTableWrapper>
			<Table
				data-testid="new-order-save-draft-table"
				aria-label="Save draft table"
			>
				<SortableTableHead
					orderBy={orderBy}
					order={order}
					onRequestSort={handleRequestSort}
					headCells={filteredHeadCells}
				/>
				<TableBody>
					{filteredDraftRows?.map((draft) => (
						<TableRow key={draft.draftOrderId}>
							<SaveDraftTableRow
								draft={draft}
								isInternalDesigner={isInternalDesigner}
							/>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</SaveDraftTableWrapper>
	) : (
		<SaveDraftNoDraftsFilter SaveDraftTemplateModalDetails={saveDraftDetails.modalDetails} />
	);

	const skeletonTabLabels = [
		SaveDraftDraftTab,
		...(isInternalDesigner ? [SaveDraftQuoteTab] : []),
		SaveDraftTemplateTab
	];

	return (
		<>
			{isFetchingDrafts ? (
				<TableSkeletonWithTabs
					tabLabels={skeletonTabLabels}
					disclaimerText={saveDraftDetails.subHeader}
				/>
			) : (
				<SaveDraftTableContainer>
					<SaveDraftExteriorTableHeader>
						<div>
							<Tabs
								value={value}
								onChange={handleChange}
								aria-label="Save draft table tabs"
							>
								<Tab
									value={DraftType.Draft}
									label={SaveDraftDraftTab}
									data-testid="new-order-save-draft-draft-tab"
								/>
								{isInternalDesigner && (
									<Tab
										value={DraftType.Quote}
										label="QUOTES"
										data-testid="new-order-save-draft-quote-tab"
									/>
								)}
								{!isLowesUser && (
									<Tab
										value={DraftType.Template}
										label={SaveDraftTemplateTab}
										data-testid="new-order-save-draft-template-tab"
									/>
								)}
							</Tabs>
						</div>
						<div>
							{allDrafts && (
								<SaveDraftTableFilters
									activeView={saveDraftDetails.filterConfig.activeView}
									filterOptions={saveDraftDetails.filterConfig.filterOptions}
									setFilterOptions={saveDraftDetails.filterConfig.setFilterOptions}
									drafts={allDrafts}
								/>
							)}

							<SuggestedSearch
								ariaLabel="search-drafts"
								dataTestId="new-order-save-draft-table-search"
								dropDownOpenOnChar={3}
								placeholder={saveDraftDetails.searchPlaceholder}
								handleSearch={(event, values) => handleDraftSearch(event, values ?? "")}
								id="new-order-draft-table-search-drafts"
								searchOptions={searchOptions ?? []}
								searchValue={searchValue ?? ""}
								size="small"
							/>
						</div>
					</SaveDraftExteriorTableHeader>

					{saveDraftDetails.subHeader && (
						<SaveDraftTableDisclaimer>{saveDraftDetails.subHeader}</SaveDraftTableDisclaimer>
					)}

					{getDraftsError && "status" in getDraftsError && getDraftsError.status === 400 && (
						<SaveDraft400Error />
					)}

					{sortedDrafts && sortedDrafts?.length > 0 && isAvailable ? (
						draftRows
					) : (
						<SaveDraftNoDrafts draftType={draftViewType} />
					)}
				</SaveDraftTableContainer>
			)}
		</>
	);
};

export default SaveDraftTable;
